import { CUSTOM_FILTERS } from "@/config/list";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { ListFilters } from "@/types/list";

const EVENT_TYPES = [
	{ name: "OverFlow", id: 1 },
	{ name: "Rain", id: 2 },
	{ name: "Blockage", id: 3 },
] as const;

type EventTypeName = (typeof EVENT_TYPES)[number]["name"];

const EVENT_TYPE_COLORS: Record<EventTypeName, string> = {
	Rain: "bg-sky-400",
	OverFlow: "bg-red-500",
	Blockage: "bg-amber-800",
};

const EventTypeDot = ({ name }: { name: EventTypeName }) => (
	<div className="flex items-center gap-2 text-xs">
		<span className={`w-[10px] h-[10px] rounded-full inline-block ${EVENT_TYPE_COLORS[name]}`} />
		<span>{name}</span>
	</div>
);

type EventTypeFilterProps = {
	filters: ListFilters;
};

const parseSelectedIds = (rawValue: unknown): number[] => {
	if (Array.isArray(rawValue)) return rawValue.map(Number);
	if (rawValue != null) return [Number(rawValue)];
	return [];
};

export const EventTypeFilter = ({ filters }: EventTypeFilterProps) => {
	const rawValue = filters.getFilter(CUSTOM_FILTERS.eventType);
	const selectedIds = parseSelectedIds(rawValue);

	const toggle = (id: number) => {
		const next = selectedIds.includes(id) ? selectedIds.filter(v => v !== id) : [...selectedIds, id];
		if (next.length === 0) {
			filters.clearFilter(CUSTOM_FILTERS.eventType);
		} else {
			filters.setFilter(CUSTOM_FILTERS.eventType, next);
		}
	};

	const clearAll = () => {
		filters.clearFilter(CUSTOM_FILTERS.eventType);
	};

	const selectedNames = EVENT_TYPES.filter(e => selectedIds.includes(e.id));

	return (
		<div className="relative z-10">
			<div className="flex flex-col space-y-1">
				<span className="text-xs font-medium text-muted-foreground">Event Type</span>

				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant="outline"
							size="sm"
							className="justify-start gap-2"
							aria-pressed={selectedIds.length > 0}
						>
							{selectedNames.length > 0 ? (
								<div className="flex items-center gap-1">
									{selectedNames.map(t => (
										<EventTypeDot key={t.id} name={t.name} />
									))}
								</div>
							) : (
								<span className="font-semibold text-muted-foreground">All</span>
							)}
							<ChevronDown className="ml-2 h-4 w-4 opacity-50" />
						</Button>
					</DropdownMenuTrigger>

					<DropdownMenuContent align="start">
						<DropdownMenuItem
							onSelect={clearAll}
							className={selectedIds.length === 0 ? "font-semibold bg-muted" : ""}
						>
							All
						</DropdownMenuItem>

						{EVENT_TYPES.map(type => (
							<DropdownMenuItem
								key={type.id}
								onSelect={() => toggle(type.id)}
								className={selectedIds.includes(type.id) ? "font-semibold bg-muted" : ""}
							>
								<EventTypeDot name={type.name} />
							</DropdownMenuItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</div>
	);
};
